/**
 * Company Item Store
 *
 * Zustand store for Company Resources
 * Manages state for items assigned to the company
 */

import { create } from 'zustand'
import { toast } from 'react-hot-toast'
import companyItemService from '@services/companyItemService'

const useCompanyItemStore = create((set, get) => ({
  // State
  items: [],
  selectedItem: null,
  loading: false,
  error: null,

  // Filters
  filters: {
    search: '',
    type: 'all',
  },

  // Pagination
  pagination: {
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  },

  // Statistics
  stats: {
    total: 0,
    with_updates: 0,
    byType: {},
  },

  // ========================================
  // READ ACTIONS
  // ========================================

  /**
   * Fetch items assigned to company
   */
  fetchItems: async () => {
    const { filters, pagination } = get()
    set({ loading: true, error: null })

    try {
      const response = await companyItemService.getAll(filters, pagination)
      set({
        items: response.data || [],
        pagination: response.pagination || pagination,
        loading: false,
      })
    } catch (error) {
      console.error('Failed to fetch resources:', error)
      set({ error: error.message, loading: false })
      toast.error('Failed to fetch resources')
    }
  },

  /**
   * Fetch single item by ID
   */
  fetchItem: async (id) => {
    set({ loading: true, error: null })

    try {
      const response = await companyItemService.getById(id)
      set({ selectedItem: response.data, loading: false })
      return response.data
    } catch (error) {
      console.error('Failed to fetch resource:', error)
      set({ error: error.message, loading: false })
      toast.error('Failed to fetch resource')
      return null
    }
  },

  /**
   * Fetch statistics
   */
  fetchStats: async () => {
    try {
      const response = await companyItemService.getStats()
      set({ stats: response.data || {} })
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    }
  },

  // ========================================
  // WRITE ACTIONS
  // ========================================

  /**
   * Download item file
   */
  downloadItem: async (id) => {
    try {
      await companyItemService.download(id)
      toast.success('Download started')
    } catch (error) {
      console.error('Failed to download:', error)
      toast.error('Failed to download file')
    }
  },

  /**
   * Update to latest version
   */
  updateToLatestVersion: async (id) => {
    set({ loading: true, error: null })

    try {
      const response = await companyItemService.updateToLatestVersion(id)
      set((state) => ({
        items: state.items.map((item) =>
          item.item_id === id
            ? { ...item, item_version: item.current_version, has_update: false }
            : item
        ),
        selectedItem: state.selectedItem?.item_id === id
          ? { ...state.selectedItem, item_version: state.selectedItem.current_version, has_update: false }
          : state.selectedItem,
        loading: false,
      }))
      toast.success(response.message || 'Updated to latest version')
      get().fetchStats()
      return true
    } catch (error) {
      console.error('Failed to update version:', error)
      set({ error: error.message, loading: false })
      toast.error(error.message || 'Failed to update to latest version')
      return false
    }
  },

  // ========================================
  // FILTER ACTIONS
  // ========================================

  /**
   * Set search filter
   */
  setSearch: (search) => {
    set((state) => ({
      filters: { ...state.filters, search },
      pagination: { ...state.pagination, page: 1 },
    }))
  },

  /**
   * Set type filter
   */
  setTypeFilter: (type) => {
    set((state) => ({
      filters: { ...state.filters, type },
      pagination: { ...state.pagination, page: 1 },
    }))
  },

  /**
   * Reset all filters
   */
  resetFilters: () => {
    set({
      filters: {
        search: '',
        type: 'all',
      },
      pagination: { page: 1, limit: 25, total: 0, totalPages: 0 },
    })
  },

  // ========================================
  // PAGINATION ACTIONS
  // ========================================

  /**
   * Set current page
   */
  setPage: (page) => {
    set((state) => ({
      pagination: { ...state.pagination, page },
    }))
  },

  /**
   * Set items per page
   */
  setLimit: (limit) => {
    set((state) => ({
      pagination: { ...state.pagination, limit, page: 1 },
    }))
  },

  /**
   * Go to next page
   */
  nextPage: () => {
    const { pagination } = get()
    if (pagination.page < pagination.totalPages) {
      set((state) => ({
        pagination: { ...state.pagination, page: state.pagination.page + 1 },
      }))
    }
  },

  /**
   * Go to previous page
   */
  prevPage: () => {
    const { pagination } = get()
    if (pagination.page > 1) {
      set((state) => ({
        pagination: { ...state.pagination, page: state.pagination.page - 1 },
      }))
    }
  },

  // ========================================
  // SELECTION ACTIONS
  // ========================================

  /**
   * Select an item
   */
  selectItem: (item) => {
    set({ selectedItem: item })
  },

  /**
   * Clear selection
   */
  clearSelection: () => {
    set({ selectedItem: null })
  },

  /**
   * Clear error
   */
  clearError: () => {
    set({ error: null })
  },
}))

export default useCompanyItemStore
